import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { useLocale } from './LocaleContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const defaultSiteSettings = {
  general: {
    siteTitle: 'هریاد',
    tagline: 'با هریاد، همیشه در یاد',
    logoUrl: '/logo/logo.webp',
    faviconUrl: '',
    primaryDark: '#03045E',
    primary: '#0077B6',
    primaryLight: '#00B4D8',
    secondary: '#90E0EF',
    secondaryLight: '#CAF0F8',
    footerDescription: 'جایی که کسب‌وکارها آنلاین می‌شوند، رشد می‌کنند و برای همیشه در یاد می‌مانند.',
    copyrightText: 'تمامی حقوق محفوظ است.',
    trustSealUrl: 'https://trustseal.enamad.ir/?id=703719&Code=fw6ZgIXqgeWyQVWicx2iW7vVmgdHYU60',
    trustSealImageUrl: 'https://trustseal.enamad.ir/logo.aspx?id=703719&Code=fw6ZgIXqgeWyQVWicx2iW7vVmgdHYU60',
  },
  contact: {
    phone: '044-46269296',
    mobile: '',
    supportEmail: 'info@haryad.com',
    address: 'ایران، آذربایجان غربی، بوکان، خیابان خیام، کوچه فردوس 2',
    workingHours: 'شنبه تا پنجشنبه: 9 صبح تا 5 عصر',
    mapEmbedUrl: '',
    social: { instagram: '', telegram: 'https://t.me/Haryad_com', whatsapp: '', linkedin: '', x: '', youtube: '' },
  },
  seo: {
    defaultTitle: 'هریاد | طراحی و توسعه دیجیتال',
    defaultDescription: 'هریاد؛ طراحی وب‌سایت، رابط کاربری، هویت بصری و توسعه افزونه‌های حرفه‌ای.',
    defaultShareImage: '',
    googleAnalyticsId: '',
    googleTagManagerId: '',
  },
  status: {
    maintenanceEnabled: false,
    maintenanceTitle: 'به‌زودی بازمی‌گردیم',
    maintenanceMessage: 'در حال به‌روزرسانی وب‌سایت هستیم. لطفاً کمی بعد دوباره مراجعه کنید.',
    registrationEnabled: true,
  },
  payment: { enabled: true, title: 'پرداخت الکترونیک سپهر', minimumAmount: 100 },
  store: { downloadLimit: 0, downloadValidityDays: 0, maxProductFileSizeMb: 200 },
};

const mergeSettings = (base, incoming) => ({
  ...base,
  ...incoming,
  general: { ...base.general, ...(incoming?.general || {}) },
  contact: {
    ...base.contact,
    ...(incoming?.contact || {}),
    social: { ...base.contact.social, ...(incoming?.contact?.social || {}) },
  },
  seo: { ...base.seo, ...(incoming?.seo || {}) },
  status: { ...base.status, ...(incoming?.status || {}) },
  payment: { ...base.payment, ...(incoming?.payment || {}) },
  store: { ...base.store, ...(incoming?.store || {}) },
});

export const resolveSettingMediaUrl = (value) => (
  value && value.startsWith('/uploads/') ? `${API_URL}${value}` : value
);

const SiteSettingsContext = createContext(null);

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) throw new Error('useSiteSettings must be used within SiteSettingsProvider');
  return context;
};

const upsertMeta = (key, value, attribute = 'name') => {
  if (!value) return;
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute('content', value);
};

export const SiteSettingsProvider = ({ children }) => {
  const { locale } = useLocale();
  const [settings, setSettings] = useState(defaultSiteSettings);
  const [settingsLoading, setSettingsLoading] = useState(true);

  const refreshSettings = useCallback(async () => {
    try {
      const response = await api.get(`/api/settings/public?locale=${encodeURIComponent(locale.code)}`);
      if (response.success && response.data) {
        setSettings((current) => mergeSettings({ ...defaultSiteSettings, ...current }, response.data));
      }
    } catch (_) {
      // Defaults keep the public site usable if settings cannot be fetched.
    } finally {
      setSettingsLoading(false);
    }
  }, [locale.code]);

  useEffect(() => { refreshSettings(); }, [refreshSettings]);

  useEffect(() => {
    const colors = settings.general;
    document.documentElement.style.setProperty('--primary-dark', colors.primaryDark);
    document.documentElement.style.setProperty('--primary', colors.primary);
    document.documentElement.style.setProperty('--primary-light', colors.primaryLight);
    document.documentElement.style.setProperty('--secondary', colors.secondary);
    document.documentElement.style.setProperty('--secondary-light', colors.secondaryLight);

    upsertMeta('description', settings.seo.defaultDescription);
    upsertMeta('og:title', settings.seo.defaultTitle || settings.general.siteTitle, 'property');
    upsertMeta('og:description', settings.seo.defaultDescription, 'property');
    if (settings.seo.defaultShareImage) {
      upsertMeta('og:image', resolveSettingMediaUrl(settings.seo.defaultShareImage), 'property');
    }

    const faviconUrl = resolveSettingMediaUrl(settings.general.faviconUrl);
    if (faviconUrl) {
      let favicon = document.head.querySelector('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = faviconUrl;
    }
  }, [settings]);

  useEffect(() => {
    const gaId = /^G-[A-Z0-9]+$/i.test(settings.seo.googleAnalyticsId || '')
      ? settings.seo.googleAnalyticsId
      : '';
    const externalId = 'haryad-google-analytics';
    const inlineId = 'haryad-google-analytics-inline';
    document.getElementById(externalId)?.remove();
    document.getElementById(inlineId)?.remove();
    if (!gaId) return;
    const external = document.createElement('script');
    external.id = externalId;
    external.async = true;
    external.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
    const inline = document.createElement('script');
    inline.id = inlineId;
    inline.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
    document.head.append(external, inline);
    return () => { external.remove(); inline.remove(); };
  }, [settings.seo.googleAnalyticsId]);

  useEffect(() => {
    const gtmId = /^GTM-[A-Z0-9]+$/i.test(settings.seo.googleTagManagerId || '')
      ? settings.seo.googleTagManagerId
      : '';
    const scriptId = 'haryad-google-tag-manager';
    document.getElementById(scriptId)?.remove();
    if (!gtmId) return;
    const script = document.createElement('script');
    script.id = scriptId;
    script.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(script);
    return () => script.remove();
  }, [settings.seo.googleTagManagerId]);

  const value = useMemo(() => ({ settings, settingsLoading, refreshSettings, setSettings }), [settings, settingsLoading, refreshSettings]);
  return <SiteSettingsContext.Provider value={value}>{children}</SiteSettingsContext.Provider>;
};
